import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Reveal-on-scroll container. Transition-based; see restingTransform below.
// Animation types
export type AnimationType = 'slideInFromBottom' | 'slideInFromLeft' | 'slideInFromRight' | 'fadeIn' | 'scaleIn' | 'rotateIn' | 'blurIn' | 'bounceIn' | 'slideAndFade';

interface ScrollAnimationProps {
  children: React.ReactNode;
  animation?: AnimationType;
  delay?: number;
  duration?: number;
  threshold?: number;
  triggerOnce?: boolean;
  className?: string;
}

/**
 * The resting transform each variant animates *from*.
 *
 * These are transitioned, not keyframed, and opacity never drops below 0.99.
 * The previous implementation ran a keyframe animation whose `from` state was
 * `opacity: 0` with `animation-fill-mode: forwards`. Because `forwards` fills
 * only the end state, an element sat fully visible at 0.99 through its
 * animation-delay, then snapped to 0 the instant the animation began and faded
 * back in — a visible flash on every block as it entered the viewport, on all
 * 95 usages. Transitioning from 0.99 cannot flash, which is why the sibling
 * SimpleFadeIn component never had this problem.
 */
const restingTransform = (type: AnimationType): string => {
  switch (type) {
    case 'slideInFromLeft':  return 'translateX(-40px)';
    case 'slideInFromRight': return 'translateX(40px)';
    case 'scaleIn':          return 'scale(0.94)';
    case 'rotateIn':         return 'rotate(-3deg)';
    case 'bounceIn':         return 'scale(0.92)';
    case 'fadeIn':           return 'none';
    case 'blurIn':           return 'none';
    case 'slideAndFade':
    case 'slideInFromBottom':
    default:                 return 'translateY(40px)';
  }
};

const AnimatedContainer = styled.div<{
  $isVisible: boolean;
  $animation: AnimationType;
  $delay: number;
  $duration: number;
}>`
  /* Never below 0.99: if the observer never fires, content is still readable,
     and there is no value the element can flash down to. */
  opacity: ${props => (props.$isVisible ? 1 : 0.99)};
  transform: ${props => (props.$isVisible ? 'none' : restingTransform(props.$animation))};
  ${props => props.$animation === 'blurIn'
    && `filter: blur(${props.$isVisible ? '0px' : '6px'});`}
  transition:
    opacity ${props => props.$duration}ms ease-out ${props => props.$delay}ms,
    transform ${props => props.$duration}ms ease-out ${props => props.$delay}ms,
    filter ${props => props.$duration}ms ease-out ${props => props.$delay}ms;
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
    transition: none !important;
  }

  html[data-reduce-motion='true'] & {
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
    transition: none !important;
  }
`;

export const ScrollAnimation: React.FC<ScrollAnimationProps> = ({
  children,
  animation = 'slideInFromBottom',
  delay = 0,
  duration = 600,
  threshold = 0.1,
  triggerOnce = true,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, triggerOnce]);

  return (
    <AnimatedContainer
      ref={ref}
      $isVisible={isVisible}
      $animation={animation}
      $delay={delay}
      $duration={duration}
      className={className}
    >
      {children}
    </AnimatedContainer>
  );
};

// Hook for manual scroll animation control
export const useScrollAnimation = (
  threshold: number = 0.1,
  triggerOnce: boolean = true
) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, triggerOnce]);

  return { ref, isVisible };
};

export default ScrollAnimation;